'use client';

import { useEffect, useMemo, useState } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { useDoc } from '@/firebase/firestore/use-doc';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Save, Mail, MessageSquare, Link2, Sparkles } from 'lucide-react';

interface OnboardingConfig {
  emailSubject?: string;
  emailMessage?: string;
  discordWelcomeMessage?: string;
  discordInviteMessage?: string;
  inviteLink?: string;
}

const DEFAULTS: Required<OnboardingConfig> = {
  emailSubject: 'Bienvenido a Jartiland — Tus credenciales de acceso',
  emailMessage:
    '¡Enhorabuena {nickname}! Tu solicitud ha sido aceptada. A continuación tienes tus credenciales para acceder a Jartinet. Te recomendamos cambiar la contraseña tras iniciar sesión.',
  discordWelcomeMessage:
    '¡Bienvenido a Jartiland, {nickname}! 🎉 Tu cuenta de Jartinet ya está activa. Revisa tu correo para ver tus credenciales de acceso.',
  discordInviteMessage:
    'Aún no estás en nuestro servidor de Discord. ¡Únete a la comunidad cofrade de Jartiland!',
  inviteLink: '',
};

export function OnboardingSettings() {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Required<OnboardingConfig>>(DEFAULTS);
  const [hydrated, setHydrated] = useState(false);

  const ref = useMemo(() => (db ? doc(db, 'app_settings', 'onboarding') : null), []);
  const { data, isLoading } = useDoc<OnboardingConfig>(ref);

  useEffect(() => {
    if (isLoading || hydrated) return;
    setForm({ ...DEFAULTS, ...(data || {}) });
    setHydrated(true);
  }, [data, isLoading, hydrated]);

  const update = (key: keyof OnboardingConfig, value: string) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!db) return;
    setSaving(true);
    try {
      await setDoc(doc(db, 'app_settings', 'onboarding'), form, { merge: true });
      toast({ title: 'Guardado', description: 'Los mensajes de onboarding se han actualizado.' });
    } catch (e: any) {
      toast({ title: 'Error', description: e?.message || 'No se pudo guardar.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (isLoading && !hydrated) {
    return (
      <div className="flex items-center justify-center py-10 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-white/8 bg-card/40 p-5">
        <div className="mb-3 flex items-center gap-2 text-amber-400">
          <Mail className="h-4 w-4" />
          <h4 className="text-sm font-semibold uppercase tracking-wide">Correo de credenciales</h4>
        </div>
        <div className="space-y-3">
          <div>
            <Label htmlFor="emailSubject" className="text-xs text-muted-foreground">Asunto</Label>
            <Input id="emailSubject" value={form.emailSubject} onChange={e => update('emailSubject', e.target.value)} />
          </div>
          <div>
            <Label htmlFor="emailMessage" className="text-xs text-muted-foreground">Mensaje (intro antes de las credenciales)</Label>
            <Textarea id="emailMessage" rows={3} value={form.emailMessage} onChange={e => update('emailMessage', e.target.value)} />
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/8 bg-card/40 p-5">
        <div className="mb-3 flex items-center gap-2 text-indigo-400">
          <MessageSquare className="h-4 w-4" />
          <h4 className="text-sm font-semibold uppercase tracking-wide">Discord</h4>
        </div>
        <div className="space-y-3">
          <div>
            <Label htmlFor="discordWelcomeMessage" className="text-xs text-muted-foreground">DM de bienvenida</Label>
            <Textarea id="discordWelcomeMessage" rows={2} value={form.discordWelcomeMessage} onChange={e => update('discordWelcomeMessage', e.target.value)} />
          </div>
          <div>
            <Label htmlFor="discordInviteMessage" className="text-xs text-muted-foreground">DM de invitación (si no está en el server)</Label>
            <Textarea id="discordInviteMessage" rows={2} value={form.discordInviteMessage} onChange={e => update('discordInviteMessage', e.target.value)} />
          </div>
          <div>
            <Label htmlFor="inviteLink" className="text-xs text-muted-foreground flex items-center gap-1">
              <Link2 className="h-3 w-3" /> Enlace de invitación al Discord
            </Label>
            <Input id="inviteLink" placeholder="https://discord.gg/..." value={form.inviteLink} onChange={e => update('inviteLink', e.target.value)} />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-amber-400" />
          Usa <code className="rounded bg-muted px-1 py-0.5 text-[0.7rem]">{'{nickname}'}</code> para insertar el nombre del usuario.
        </p>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Guardar mensajes
        </Button>
      </div>
    </div>
  );
}
